import React from 'react';
import { Link } from 'react-router-dom';
import useSolutions from '../../hooks/useSolutions';
import { getImageUrl } from '../../services/api';

const ServicesGrid = () => {
  const { solutions, loading, error } = useSolutions();

  // Helper function to render description text
  const renderDescription = (description) => {
    if (!description) return '';
    
    if (typeof description === 'string') {
      return description;
    }
    
    if (Array.isArray(description)) {
      return description
        .map(block => {
          if (block.children) {
            return block.children.map(child => child.text || '').join('');
          }
          return '';
        })
        .join(' ');
    }
    
    return '';
  };



  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-catalina-blue"></div>
            <p className="mt-4 text-gray-600">Loading solutions...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-red-600">
            <p>Error loading solutions. Please try again later.</p>
          </div>
        </div>
      </section>
    );
  }

  // Define the order of solutions
  const solutionOrder = [
    'Material Handling Solutions',
    'Access & Safety Solutions',
    'Warehouse Equipment Solutions',
    'Customized Supply Solutions'
  ];

  // Sort solutions according to the specified order
  const sortedSolutions = [...solutions].sort((a, b) => {
    const indexA = solutionOrder.indexOf(a.Title);
    const indexB = solutionOrder.indexOf(b.Title);
    
    // If both are in the order list, sort by their position
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    
    // If only a is in the list, it comes first
    if (indexA !== -1) return -1;
    // If only b is in the list, it comes first
    if (indexB !== -1) return 1;
    
    // If neither are in the list, keep original order
    return 0;
  });

  // Display only first 3 solutions on the main services page
  const displaySolutions = sortedSolutions.slice(0, 3);

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-catalina-blue mb-4">Our Solutions</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Discover our comprehensive range of material handling solutions designed to meet your specific needs
          </p>
        </div>

        {displaySolutions.length === 0 ? (
          <div className="text-center text-gray-600">
            <p>No solutions available at the moment.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {displaySolutions.map((solution) => {
                const imageUrl = getImageUrl(solution.Image, 'small');
                return (
                  <div
                    key={solution.id}
                    className="bg-white rounded-3xl shadow-lg p-8 hover:shadow-xl transition duration-300"
                  >
                    {imageUrl && (
                      <div className="mb-6 rounded-2xl overflow-hidden h-48 w-full bg-gray-200">
                        <img
                          src={imageUrl}
                          alt={solution.Title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <h3 className="text-2xl font-bold text-catalina-blue mb-4">{solution.Title}</h3>
                    <p className="text-gray-600 mb-6 leading-relaxed line-clamp-4">
                      {renderDescription(solution.Description)}
                    </p>
                    <Link
                      to={`/solution/${solution.slug}`}
                      className="inline-flex items-center text-tango font-semibold hover:text-catalina-blue transition duration-300"
                    >
                      Learn More
                      <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </Link>
                  </div>
                );
              })}
            </div>

            {sortedSolutions.length > 3 && (
              <div className="text-center mt-12">
                <Link
                  to="/solutions-we-provide/all"
                  className="inline-block bg-tango text-white px-8 py-3 rounded-full font-semibold hover:bg-catalina-blue transition duration-300"
                >
                  View All Solutions
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default ServicesGrid;